import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_PAGE, BG_CARD } from '../components/legacy-ball/table-management/designTokens';

interface HelpMessage {
  id: string;
  sender: 'guest' | 'organizer';
  senderName: string;
  body: string;
  createdAt: string;
}

interface HelpInquiry {
  id: string;
  code: string;
  guestName: string;
  guestPhone: string;
  topic: string | null;
  status: 'Open' | 'InProgress' | 'Resolved';
  ownerId: string | null;
  ownerName: string | null;
  createdAt: string;
  updatedAt: string;
  messages: HelpMessage[];
}

interface TeamMember {
  userId: string;
  fullName: string;
  email: string | null;
  role: string;
  addedAt: string;
}

interface UserResult {
  id: string;
  fullName: string;
  email: string | null;
  role: string;
}

type StatusFilter = 'active' | 'all' | 'Open' | 'InProgress' | 'Resolved';

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
}

export function HelpDeskPage() {
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [inquiries, setInquiries] = useState<HelpInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Authorization check — must be Live Help Desk staff
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient<{ isStaff: boolean }>('/help-desk/admin/authorization');
        if (!cancelled) setAuthorized(res.isStaff);
      } catch {
        if (!cancelled) setAuthorized(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<HelpInquiry | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Team management
  const [showTeam, setShowTeam] = useState(false);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [teamError, setTeamError] = useState('');

  const fetchList = useCallback(async () => {
    try {
      const statusParam = statusFilter === 'active' ? undefined : statusFilter;
      const query = statusParam ? `?status=${statusParam}` : '';
      const data = await apiClient<HelpInquiry[]>(`/help-desk/admin/inquiries${query}`);
      // "active" = Open + InProgress
      const filtered = statusFilter === 'active'
        ? data.filter((i) => i.status !== 'Resolved')
        : data;
      setInquiries(filtered);
      setError('');
    } catch {
      setError('Failed to load help inquiries.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchThread = useCallback(async (id: string) => {
    try {
      const data = await apiClient<HelpInquiry>(`/help-desk/admin/inquiries/${id}`);
      setSelected(data);
    } catch {
      // ignore, retry on poll
    }
  }, []);

  // Poll the list (only once authorized)
  useEffect(() => {
    if (!authorized) return;
    fetchList();
    const timer = setInterval(fetchList, 10000);
    return () => clearInterval(timer);
  }, [authorized, fetchList]);

  // Poll the open thread
  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    fetchThread(selectedId);
    const timer = setInterval(() => fetchThread(selectedId), 5000);
    return () => clearInterval(timer);
  }, [selectedId, fetchThread]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selected?.messages.length]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedId) return;
    setSending(true);
    try {
      const data = await apiClient<HelpInquiry>(`/help-desk/admin/inquiries/${selectedId}/messages`, {
        method: 'POST',
        body: { message: replyText.trim() },
      });
      setSelected(data);
      setReplyText('');
      fetchList();
    } catch {
      setError('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedId) return;
    try {
      const data = await apiClient<HelpInquiry>(`/help-desk/admin/inquiries/${selectedId}/resolve`, { method: 'PATCH' });
      setSelected(data);
      fetchList();
    } catch {
      setError('Failed to resolve.');
    }
  };

  const handleReopen = async () => {
    if (!selectedId) return;
    try {
      const data = await apiClient<HelpInquiry>(`/help-desk/admin/inquiries/${selectedId}/reopen`, { method: 'PATCH' });
      setSelected(data);
      fetchList();
    } catch {
      setError('Failed to reopen.');
    }
  };

  // ── Team management ──
  const fetchTeam = useCallback(async () => {
    setTeamLoading(true);
    setTeamError('');
    try {
      const data = await apiClient<TeamMember[]>('/help-desk/admin/team');
      setTeam(data);
    } catch {
      setTeamError('Failed to load team.');
    } finally {
      setTeamLoading(false);
    }
  }, []);

  const openTeam = () => {
    setShowTeam(true);
    fetchTeam();
  };

  // Debounced user search
  useEffect(() => {
    if (!showTeam) return;
    if (userSearch.trim().length < 2) { setUserResults([]); return; }
    let cancelled = false;
    setSearchingUsers(true);
    const t = setTimeout(async () => {
      try {
        const data = await apiClient<UserResult[]>(`/help-desk/admin/user-search?q=${encodeURIComponent(userSearch.trim())}`);
        if (!cancelled) setUserResults(data);
      } catch {
        if (!cancelled) setUserResults([]);
      } finally {
        if (!cancelled) setSearchingUsers(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [userSearch, showTeam]);

  const handleAddMember = async (userId: string) => {
    setTeamError('');
    try {
      await apiClient('/help-desk/admin/team', { method: 'POST', body: { userId } });
      setUserSearch('');
      setUserResults([]);
      await fetchTeam();
    } catch (err: any) {
      setTeamError(err?.message || 'Failed to add member.');
    }
  };

  const handleRemoveMember = async (userId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from the Live Help Desk team?`)) return;
    setTeamError('');
    try {
      await apiClient(`/help-desk/admin/team/${userId}`, { method: 'DELETE' });
      await fetchTeam();
    } catch (err: any) {
      setTeamError(err?.message || 'Failed to remove member.');
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      Open: { bg: '#fef3c7', text: '#92400e' },
      InProgress: { bg: '#dbeafe', text: '#1e40af' },
      Resolved: { bg: '#dcfce7', text: '#166534' },
    };
    const c = colors[status] || { bg: '#f3f4f6', text: '#6b7280' };
    return (
      <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: c.bg, color: c.text }}>
        {status === 'InProgress' ? 'In Progress' : status}
      </span>
    );
  };

  // Authorization gate
  if (authorized === null) {
    return <div style={styles.page}><div style={styles.emptyState}>Checking access...</div></div>;
  }
  if (authorized === false) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.rightPane, maxWidth: '520px', margin: '2rem auto', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔒</div>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: TEXT_DARK, margin: '0 0 0.5rem' }}>Live Help Desk — Members Only</h2>
          <p style={{ fontSize: '0.9rem', color: TEXT_MUTED, lineHeight: 1.5, margin: 0 }}>
            You're not on the Live Help Desk team yet. An organizer can add you from
            <strong> Discussions → Live Help Desk</strong> to give you access to answer guest chats.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.pageTitle}>🪘 Guest Help Desk</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setSelectedId(null); }}
            style={styles.select}
            aria-label="Filter by status"
          >
            <option value="active">Active (Open + In Progress)</option>
            <option value="Open">Open</option>
            <option value="InProgress">In Progress</option>
            <option value="Resolved">Resolved</option>
            <option value="all">All</option>
          </select>
          <a
            href="/login-troubleshooting-guide.html"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.guideLink}
            aria-label="Open login troubleshooting guide in a new tab"
          >
            🔐 Login Troubleshooting
          </a>
          <button style={styles.teamBtn} onClick={openTeam} aria-label="Manage help desk team">
            👥 Manage Team
          </button>
        </div>
      </div>

      {/* Manage Team modal */}
      {showTeam && (
        <div
          style={styles.modalOverlay}
          onClick={(e) => e.target === e.currentTarget && setShowTeam(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Manage help desk team"
        >
          <div style={styles.modalSheet}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: TEXT_DARK, margin: 0 }}>👥 Live Help Desk Team</h2>
                <p style={{ fontSize: '0.78rem', color: TEXT_MUTED, margin: '2px 0 0' }}>
                  Members here can answer guest login-help chats. Admins & Organizers always have access.
                </p>
              </div>
              <button style={styles.modalClose} onClick={() => setShowTeam(false)} aria-label="Close">✕</button>
            </div>

            <div style={{ padding: '1rem 1.25rem', overflowY: 'auto' }}>
              {teamError && <div style={styles.errorBox}>{teamError}</div>}

              {/* Add member */}
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_DARK, display: 'block', marginBottom: '0.35rem' }}>
                Add a team member
              </label>
              <input
                style={styles.teamSearchInput}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or email..."
                aria-label="Search users"
              />
              {userSearch.trim().length >= 2 && (
                <div style={styles.searchResults}>
                  {searchingUsers ? (
                    <div style={styles.searchEmpty}>Searching...</div>
                  ) : userResults.length === 0 ? (
                    <div style={styles.searchEmpty}>No matching users (or already on team).</div>
                  ) : (
                    userResults.map((u) => (
                      <div key={u.id} style={styles.searchRow}>
                        <div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>{u.fullName}</div>
                          <div style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>{u.email || '—'} · {u.role}</div>
                        </div>
                        <button style={styles.addBtn} onClick={() => handleAddMember(u.id)} aria-label={`Add ${u.fullName}`}>+ Add</button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Current team */}
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_DARK, margin: '1.25rem 0 0.5rem' }}>
                Current team ({team.length})
              </div>
              {teamLoading ? (
                <div style={styles.searchEmpty}>Loading...</div>
              ) : team.length === 0 ? (
                <div style={styles.searchEmpty}>No one added yet. Admins & Organizers still have access.</div>
              ) : (
                team.map((m) => (
                  <div key={m.userId} style={styles.teamRow}>
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>{m.fullName}</div>
                      <div style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>{m.email || '—'} · {m.role}</div>
                    </div>
                    <button style={styles.removeBtn} onClick={() => handleRemoveMember(m.userId, m.fullName)} aria-label={`Remove ${m.fullName}`}>Remove</button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.splitLayout}>
        {/* Left — conversation list (each guest session = a topic) */}
        <div style={styles.leftPane}>
          <div style={styles.listHeader}>
            🪘 Live Help Desk · {inquiries.filter((i) => i.status !== 'Resolved').length} active
          </div>
          {loading ? (
            <div style={styles.emptyState}>Loading...</div>
          ) : inquiries.length === 0 ? (
            <div style={styles.emptyState}>No help requests right now.</div>
          ) : (
            inquiries.map((i) => {
              const lastMsg = i.messages[i.messages.length - 1];
              // "Needs reply" = last message is from the guest and this thread isn't open
              const needsReply = lastMsg?.sender === 'guest' && selectedId !== i.id && i.status !== 'Resolved';
              return (
                <div
                  key={i.id}
                  onClick={() => setSelectedId(i.id)}
                  style={{ ...styles.listItem, backgroundColor: selectedId === i.id ? '#eff6ff' : (needsReply ? '#fffdf5' : undefined) }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedId(i.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '0.9rem', color: TEXT_DARK }}>
                      {needsReply && <span title="New guest message" style={styles.unreadDot} />}
                      {i.guestName}
                    </span>
                    {statusBadge(i.status)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '2px' }}>
                    {i.guestPhone} · <span style={{ fontFamily: 'monospace', color: PRIMARY }}>{i.code}</span>
                  </div>
                  {i.topic && <div style={{ fontSize: '0.75rem', color: '#4338ca', marginTop: '2px' }}>{i.topic}</div>}
                  <div style={{ marginTop: '3px' }}>
                    {i.ownerName ? (
                      <span style={styles.ownerBadge} title={`Handled by ${i.ownerName}`}>👤 {i.ownerName}</span>
                    ) : (
                      <span style={styles.unassignedBadge}>● Unassigned</span>
                    )}
                  </div>
                  {lastMsg && (
                    <div style={{ fontSize: '0.75rem', color: needsReply ? TEXT_DARK : TEXT_MUTED, fontWeight: needsReply ? 600 : 400, marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lastMsg.sender === 'organizer' ? 'You: ' : ''}{lastMsg.body}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Right — chat thread */}
        <div style={styles.rightPane}>
          {!selected ? (
            <div style={styles.emptyDetail}>
              <p style={{ color: TEXT_MUTED, fontSize: '0.9rem' }}>Select a guest to start chatting</p>
            </div>
          ) : (
            <>
              <div style={styles.threadHeader}>
                <div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: TEXT_DARK }}>{selected.guestName}</div>
                  <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '2px' }}>
                    📞 {selected.guestPhone} · Code <span style={{ fontFamily: 'monospace', color: PRIMARY }}>{selected.code}</span>
                    {selected.topic ? ` · ${selected.topic}` : ''}
                  </div>
                  <div style={{ marginTop: '4px' }}>
                    {selected.ownerName ? (
                      <span style={styles.ownerBadge} title={`Handled by ${selected.ownerName}`}>👤 Handled by {selected.ownerName}</span>
                    ) : (
                      <span style={styles.unassignedBadge}>● Unassigned — first reply claims this chat</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {statusBadge(selected.status)}
                  {selected.status !== 'Resolved' ? (
                    <button style={styles.resolveBtn} onClick={handleResolve}>✓ Resolve</button>
                  ) : (
                    <button style={styles.reopenBtn} onClick={handleReopen}>↩ Reopen</button>
                  )}
                </div>
              </div>

              <div style={styles.messages}>
                {selected.messages.length === 0 ? (
                  <div style={styles.emptyState}>No messages yet. Say hello to get started.</div>
                ) : (
                  selected.messages.map((m) => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'organizer' ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                      <div style={m.sender === 'organizer' ? styles.bubbleOrganizer : styles.bubbleGuest}>
                        <div style={styles.bubbleName}>{m.sender === 'organizer' ? m.senderName : selected.guestName}</div>
                        <div>{m.body}</div>
                        <div style={styles.bubbleTime}>{formatTime(m.createdAt)}</div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSend} style={styles.inputRow}>
                <input
                  style={styles.chatInput}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply to the guest..."
                  aria-label="Reply"
                />
                <button type="submit" style={styles.sendBtn} disabled={sending || !replyText.trim()}>
                  {sending ? '...' : 'Send'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' },
  pageTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 700, color: TEXT_DARK },
  select: { padding: '0.4rem 0.75rem', borderRadius: '6px', border: `1px solid ${BORDER_LIGHT}`, fontSize: '0.82rem', color: TEXT_DARK, backgroundColor: BG_CARD },
  errorBox: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem 1rem', color: '#991b1b', marginBottom: '1rem', fontSize: '0.85rem' },
  splitLayout: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: '1.25rem', alignItems: 'start' },
  leftPane: { backgroundColor: BG_CARD, borderRadius: '10px', border: `1px solid ${BORDER_LIGHT}`, overflow: 'hidden', minHeight: '500px', maxHeight: '75vh', overflowY: 'auto' },
  rightPane: { backgroundColor: BG_CARD, borderRadius: '10px', border: `1px solid ${BORDER_LIGHT}`, minHeight: '500px', maxHeight: '75vh', display: 'flex', flexDirection: 'column' },
  listHeader: { padding: '0.6rem 1rem', borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' as const, letterSpacing: '0.04em', backgroundColor: BG_PAGE, position: 'sticky' as const, top: 0, zIndex: 1 },
  unreadDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#d97706', flexShrink: 0, display: 'inline-block' },
  ownerBadge: { display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600, backgroundColor: '#dcfce7', color: '#166534' },
  unassignedBadge: { display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600, backgroundColor: '#f3f4f6', color: '#9ca3af' },
  listItem: { padding: '0.75rem 1rem', borderBottom: `1px solid ${BORDER_LIGHT}`, cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: TEXT_MUTED, fontSize: '0.85rem' },
  emptyDetail: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '400px' },
  threadHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem 1.25rem', borderBottom: `1px solid ${BORDER_LIGHT}`, flexWrap: 'wrap' },
  messages: { flex: 1, overflowY: 'auto', padding: '1rem 1.25rem', backgroundColor: BG_PAGE },
  bubbleGuest: { maxWidth: '75%', backgroundColor: '#fff', color: TEXT_DARK, padding: '0.5rem 0.75rem', borderRadius: '14px 14px 14px 4px', fontSize: '0.88rem', lineHeight: 1.4, border: `1px solid ${BORDER_LIGHT}` },
  bubbleOrganizer: { maxWidth: '75%', backgroundColor: PRIMARY, color: '#fff', padding: '0.5rem 0.75rem', borderRadius: '14px 14px 4px 14px', fontSize: '0.88rem', lineHeight: 1.4 },
  bubbleName: { fontSize: '0.66rem', fontWeight: 700, opacity: 0.85, marginBottom: '2px' },
  bubbleTime: { fontSize: '0.62rem', opacity: 0.7, marginTop: '3px', textAlign: 'right' },
  inputRow: { display: 'flex', gap: '0.5rem', padding: '0.75rem 1.25rem', borderTop: `1px solid ${BORDER_LIGHT}` },
  chatInput: { flex: 1, padding: '0.6rem 0.75rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '20px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' },
  sendBtn: { padding: '0.6rem 1.2rem', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '20px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' },
  resolveBtn: { padding: '0.35rem 0.7rem', backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
  reopenBtn: { padding: '0.35rem 0.7rem', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
  teamBtn: { padding: '0.4rem 0.85rem', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  guideLink: { padding: '0.4rem 0.85rem', backgroundColor: '#fff', color: PRIMARY, border: `1px solid ${PRIMARY}`, borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' as const },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  modalSheet: { backgroundColor: BG_CARD, borderRadius: '14px', width: '100%', maxWidth: '480px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '1.1rem 1.25rem', borderBottom: `1px solid ${BORDER_LIGHT}` },
  modalClose: { width: '30px', height: '30px', borderRadius: '50%', background: '#f3f4f6', border: 'none', fontSize: '0.95rem', cursor: 'pointer', color: TEXT_MUTED, flexShrink: 0 },
  teamSearchInput: { width: '100%', padding: '0.55rem 0.75rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' },
  searchResults: { border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', marginTop: '0.4rem', overflow: 'hidden' },
  searchRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', borderBottom: `1px solid ${BORDER_LIGHT}` },
  searchEmpty: { padding: '0.75rem', fontSize: '0.8rem', color: TEXT_MUTED, textAlign: 'center' },
  addBtn: { padding: '0.3rem 0.7rem', backgroundColor: '#e8f5e9', color: PRIMARY, border: '1px solid #86efac', borderRadius: '6px', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
  teamRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.25rem', borderBottom: `1px solid ${BORDER_LIGHT}` },
  removeBtn: { padding: '0.3rem 0.7rem', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
};
